import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import DataGrid from './ui/DataTable';
import { Button, IconButton, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosInstance from './axiosInstance';

import { useBreadcrumbs } from './BreadcrumbBar';
import EmptyState from './ui/EmptyState';

// Leerzustände (D4)
const NoRowsOverlay = () => <EmptyState title="Keine Benutzer gefunden." />;
const NoResultsOverlay = () => (
  <EmptyState title="Nichts gefunden." hint="Such- oder Filterkriterien anpassen." />
);

// function to convert long dateString to format "YYYY-MM-DD HH:mm:ss"
const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  
  const date = new Date(dateString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};


function UserList() {
  //const [error, setError] = React.useState('');
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = React.useState(false);

    // Breadcrumbs
    useBreadcrumbs(
         [
            { label: 'Benutzer' },
          ]
    );

  React.useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users/');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDetailsClick = async (user) => {
    try {
      setSelectedUser(user);
      setDetailsDialogOpen(true);
    } catch (error) {
      console.error('Error fetching account contacts:', error);
    }
  };

  const handleDetailsClose = () => {
    setDetailsDialogOpen(false);
    setSelectedUser(null);
  };

  const userColumns = [
    { field: 'id', headerName: 'ID', width: 50 },
    {
      field: 'actions',
      headerName: 'Aktionen',
      width: 80,
      renderCell: (params) => (
        <Box>
          <IconButton
            variant="action-edit"
            onClick={() => handleDetailsClick(params.row)}
          >
            <OpenInNewOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
    { field: 'username', headerName: 'Username', flex: 1 },
    { field: 'first_name', headerName: 'Vorname', flex: 1},
    { field: 'last_name', headerName: 'Nachname', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 },
    { field: 'date_joined', 
      headerName: 'Registriert seit', 
      flex: 1, 
      valueGetter: (value) => formatDateTime(value)
    },
    { field: 'is_active', 
      headerName: 'Aktiv', 
      flex: 1,
      renderCell: (param) => {
        return param.value ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />;
      },
    }, 
    { field: 'is_superuser', 
      headerName: 'Superuser', 
      flex: 1,
      renderCell: (param) => {
        return param.value ? <CheckCircleIcon color="success" /> : null;
      },
    },
    { field: 'is_staff', 
      headerName: 'Mitarbeiter', 
      flex: 1,
      renderCell: (param) => {
        return param.value ? <CheckCircleIcon color="success" /> : null;
      },
    },

  ];

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Typography variant="h4" component="h2">Benutzer</Typography>
      </Grid>
      <Grid size={12}>
        <DataGrid
          rows={users}
          columns={userColumns}
          loading={loading}
          pageSize={5}
          rowsPerPageOptions={[5]}
          getRowId={(row) => row.id}
          autoHeight
          slots={{ noRowsOverlay: NoRowsOverlay, noResultsOverlay: NoResultsOverlay }}
          sx={{ '--DataGrid-overlayHeight': '160px' }}
        />
      </Grid>

      {/* User Details Dialog */}
      {selectedUser && (
        <Dialog open={detailsDialogOpen} onClose={handleDetailsClose} maxWidth="sm" fullWidth>
          <DialogTitle>Benutzer Details</DialogTitle>
          <DialogContent>
            <Grid container spacing={8} sx={{ mt: 1 }}>
              {/* Left Column */}
              <Grid item xs={12} sm={6}>
                <Grid container direction="column" spacing={2}>
                  <Grid item>
                    <Typography variant="subtitle2" color="text.secondary">Username</Typography>
                    <Typography variant="body1">{selectedUser?.username || '-'}</Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="subtitle2" color="text.secondary">Vorname</Typography>
                    <Typography variant="body1">{selectedUser?.first_name || '-'}</Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="subtitle2" color="text.secondary">Nachname</Typography>
                    <Typography variant="body1">{selectedUser?.last_name || '-'}</Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{selectedUser?.email || '-'}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Right Column */}
              <Grid item xs={12} sm={6}>
                <Grid container direction="column" spacing={2}>
                  <Grid item>
                    <Typography variant="subtitle2" color="text.secondary">Registriert seit</Typography>
                    <Typography variant="body1">
                      {formatDateTime(selectedUser?.date_joined)}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="subtitle2" color="text.secondary">Aktiv</Typography>
                    <Typography variant="body1">
                      {selectedUser?.is_active ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="subtitle2" color="text.secondary">Superuser</Typography>
                    <Typography variant="body1">
                      {selectedUser?.is_superuser ? <CheckCircleIcon color="success" /> : " "}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="subtitle2" color="text.secondary">Mitarbeiter</Typography>
                    <Typography variant="body1">
                      {selectedUser?.is_staff ? <CheckCircleIcon color="success" /> : " "}
                    </Typography>
                  </Grid>

                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDetailsClose}>Schließen</Button>
          </DialogActions>
        </Dialog>
      )}
      
    </Grid>
  );
}

export default UserList;